import type { TypedDocumentNode } from '@apollo/client';
import { gql } from '@apollo/client';

import { Rarity } from '@sorare/core/src/__generated__/globalTypes';
import { getEdgeRectangleShapeRarityGradients } from '@sorare/core/src/lib/colors';
import { isType, withFragments } from '@sorare/core/src/lib/gql';
import { isEnergyCurrency, isXPCurrency } from '@sorare/core/src/lib/rewards';
import { inGameCurrencyToRarity } from '@sorare/core/src/lib/scarcity';

import type { getAnyRewardConfigColors_anyRewardConfigInterface } from './__generated__/utils.graphql';

const EXPERIENCE_COLORS = {
  bg: '#1A1C0E',
  border:
    'linear-gradient(to bottom, transparent 0%, #1A1C0E 70%), linear-gradient(to right, #AA78FF, #D6E456, #AA78FF, #D6E456, #AA78FF, #D6E456, #AA78FF)',
};

export const getAnyRewardConfigColors = withFragments(
  ({
    anyRewardConfig,
    rarity,
  }: {
    anyRewardConfig: getAnyRewardConfigColors_anyRewardConfigInterface;
    rarity?: Rarity | 'mix';
  }) => {
    switch (anyRewardConfig.__typename) {
      case 'CardRewardConfig':
        return getEdgeRectangleShapeRarityGradients(anyRewardConfig.rarity);
        break;
      case 'CardShardRewardConfig':
        return getEdgeRectangleShapeRarityGradients(anyRewardConfig.rarity);
        break;
      case 'ConversionCreditRewardConfig':
        return {
          bg: '#111F1C',
          border:
            'linear-gradient(to bottom, transparent 0%, #111F1C 70%), linear-gradient(to right, #78FFE8, #EDFF95, #78FFE8, #EDFF95, #78FFE8, #EDFF95, #78FFE8)',
        };
      case 'MonetaryRewardConfig':
        return {
          bg: '#1F121C',
          border:
            'linear-gradient(to bottom, transparent 0%, #1F121C 70%), linear-gradient(to right, #78C2FF, #FF86E1, #78C2FF, #FF86E1, #78C2FF, #FF86E1, #78C2FF)',
        };
      case 'ExperienceRewardConfig':
        return EXPERIENCE_COLORS;
      case 'DeliverableItemRewardConfig':
        if (
          isType(anyRewardConfig.deliverableItem, 'WearableDeliverableItem') ||
          isType(anyRewardConfig.deliverableItem, 'JerseyDeliverableItem')
        ) {
          return getEdgeRectangleShapeRarityGradients(Rarity.common);
        }
        if (
          isType(anyRewardConfig.deliverableItem, 'ExperienceDeliverableItem')
        ) {
          return EXPERIENCE_COLORS;
        }
        if (isType(anyRewardConfig.deliverableItem, 'TicketsDeliverableItem')) {
          return {
            bg: '#07151F',
            border:
              'linear-gradient(to bottom, transparent 0%, #07151F 70%), linear-gradient(to right, #78C2FF, #FF86E1, #78C2FF, #FF86E1, #78C2FF, #FF86E1, #78C2FF)',
          };
        }
        break;
      case 'ProbabilisticBundleRewardConfig':
        return getEdgeRectangleShapeRarityGradients(
          anyRewardConfig.probabilisticBundleConfig.rarity
        );
      case 'InGameCurrencyRewardConfig':
        if (isEnergyCurrency(anyRewardConfig.currency)) {
          return getEdgeRectangleShapeRarityGradients(
            inGameCurrencyToRarity[anyRewardConfig.currency] || Rarity.common
          );
        }
        if (isXPCurrency(anyRewardConfig.currency)) {
          return getEdgeRectangleShapeRarityGradients(
            (rarity === 'mix' && rarity) ||
              inGameCurrencyToRarity[anyRewardConfig.currency] ||
              Rarity.common
          );
        }
        break;
      case 'ShopItemRewardConfig':
        return {
          bg: '',
          border: '',
        };
    }

    return {
      bg: '',
      border: '',
    };
  },
  {
    anyRewardConfigInterface: gql`
      fragment getAnyRewardConfigColors_anyRewardConfigInterface on AnyRewardConfigInterface {
        id
        ... on CardRewardConfig {
          id
          rarity
        }
        ... on CardShardRewardConfig {
          id
          rarity
        }
        ... on ProbabilisticBundleRewardConfig {
          id
          probabilisticBundleConfig {
            id
            rarity
          }
        }
        ... on InGameCurrencyRewardConfig {
          id
          currency
        }
        ... on ShopItemRewardConfig {
          id
          shopItem {
            id
            pictureUrl
          }
        }
        ... on DeliverableItemRewardConfig {
          id
          deliverableItem {
            slug
          }
        }
      }
    ` as TypedDocumentNode<getAnyRewardConfigColors_anyRewardConfigInterface>,
  }
);
